import { Piano } from "../../piano";
import { WebMidiInput } from "../midi";
import { NativeMidiInput } from "../native-midi";
import type { MidiInputAdapter } from "../midi-shared";
import { isNativeIOS } from "@/lib/platform";
import type { Disposable } from "@/lib/studio/observable";

/**
 * Owns the singleton MIDI input. Selects between `WebMidiInput` (browser)
 * and `NativeMidiInput` (Capacitor iOS) at runtime. `attach()` is idempotent
 * so the settings overlay's "Autoriser" CTA can reuse the existing input on
 * repeat clicks.
 */
export class MidiController implements Disposable {
  private _input: MidiInputAdapter | null = null;

  constructor(private readonly piano: Piano) {}

  attach(): MidiInputAdapter {
    if (this._input) {
      return this._input;
    }

    const input: MidiInputAdapter = isNativeIOS()
      ? new NativeMidiInput(this.piano)
      : new WebMidiInput(this.piano);

    this._input = input;

    // `attach()` rejects when the platform refuses access (denied MIDI
    // permission, dismissed prompt, unsupported browser). The app works fine
    // without a keyboard, so we only log it, but the rejection has to be caught
    // here or it surfaces as an unhandled runtime error. Dropping the input on
    // failure keeps the "Autoriser" CTA able to prompt again on the next call.
    input.attach().catch((error: unknown) => {
      if (this._input === input) {
        this._input = null;
      }

      console.warn("MIDI input unavailable", error);
    });

    return input;
  }

  getInput(): MidiInputAdapter | null {
    return this._input;
  }

  dispose(): void {
    if (!this._input) {
      return;
    }

    this._input.detach();
    this._input = null;
  }
}
